import React, { useState } from 'react';
import { ShieldCheck, ShieldX } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import RackSelector from '../../components/ui/RackSelector';

const InspectModal = ({ item, onClose, onSuccess }) => {
  const [action, setAction]       = useState('clear');
  const [rackLoc, setRackLoc]     = useState({ rackId: '', floor: '' });
  const [notes, setNotes]         = useState('');
  const [loading, setLoading]     = useState(false);

  const clearReady = action !== 'clear' || (!!rackLoc.rackId && !!rackLoc.floor);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clearReady) return toast.error('Select a rack and floor');
    setLoading(true);
    try {
      await api.patch(`/quarantine/${item._id}/inspect`, {
        action,
        inspectionNote: notes,
        rackId: action === 'clear' ? rackLoc.rackId : undefined,
        floor: action === 'clear' ? rackLoc.floor : undefined,
      });
      toast.success(action === 'clear' ? 'Item cleared to rack' : 'Item rejected');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Inspection failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={!!item} onClose={onClose} title="Inspect Quarantine Item">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Item summary */}
        <div className="bg-zip-surface rounded-lg p-4 space-y-2 border border-zip-border">
          {[
            ['Item', item.itemName],
            ['Quantity', `${item.quantity} ${item.unit}`],
            ['Invoice', item.inwardReceipt?.invoiceNumber ?? '—'],
            ['Reason', item.reason ?? '—'],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className="text-zip-muted">{k}</span>
              <span className="text-zip-text font-medium font-mono">{v}</span>
            </div>
          ))}
        </div>

        {/* Warning */}
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
          <ShieldX size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-zip-red">This action is irreversible. Review all details before confirming.</p>
        </div>

        {/* Action selector */}
        <div>
          <label className="block text-xs font-medium text-zip-muted mb-2">Action</label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { val: 'clear',  label: 'Clear Item',  icon: ShieldCheck, activeClass: 'bg-emerald-50 border-emerald-400 text-emerald-700' },
              { val: 'reject', label: 'Reject Item',  icon: ShieldX,     activeClass: 'bg-red-50 border-zip-red text-zip-red' },
            ].map(({ val, label, icon: Icon, activeClass }) => (
              <button
                key={val}
                type="button"
                onClick={() => setAction(val)}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-sm font-semibold transition-all duration-150
                  ${action === val ? activeClass : 'border-zip-border text-zip-muted hover:border-zip-border/60 hover:bg-zip-hover'}`}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Rack + floor (clear only) */}
        {action === 'clear' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.15 }}
          >
            <label className="block text-xs font-medium text-zip-muted mb-2">Rack &amp; Floor *</label>
            <RackSelector value={rackLoc} onChange={setRackLoc} />
          </motion.div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-xs font-medium text-zip-muted mb-1.5">Inspection Notes</label>
          <textarea
            className="zip-input resize-none"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes about the inspection…"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            type="submit"
            disabled={loading || !clearReady}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-sm text-white transition-all duration-150 disabled:opacity-60
              ${action === 'clear' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-zip-red hover:bg-[#C41424]'}`}
          >
            {loading
              ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Processing…</>
              : action === 'clear' ? 'Confirm Clear' : 'Confirm Reject'
            }
          </button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </Modal>
  );
};

export default InspectModal;
